/**
 * CR-8611: the Cost tab's Future / Historical sub-tab, owned by the dashboard
 * page so the filter panel can offer each sub-tab only its own date ranges.
 */
import { useEffect, useState } from 'react';
import type { DashboardFilterState } from './types';
import { asCostView, costViewFilters, type CostView } from './costViewRanges';
import { readSessionCostView, writeSessionCostView } from './dashboardSession';

interface UseCostView {
  costView: CostView;
  setCostView: (view: CostView) => void;
}

/**
 * The selected Cost sub-tab, remembered for the browser tab like the filters
 * (CR-5454), and the rule that keeps the date range inside its direction:
 * while the Cost tab is open, a range the sub-tab does not accept — picked on
 * another tab, typed into From/To, or left over when switching sub-tab — moves
 * to that sub-tab's default (Future: Next 20 weeks, Historical: Last 12 months).
 *
 * Entering the Cost tab applies the same rule in the page's own entry effect,
 * which also loads the cost data with the resulting range.
 *
 * @example
 * const { costView, setCostView } = useCostView(user?.id, activeTab, filters, setFilters);
 *
 * @param userId - the signed-in user (storage key)
 * @param activeTab - the dashboard tab on screen
 * @param filters - the dashboard filter state
 * @param setFilters - writes the filter state
 * @returns the sub-tab and its setter
 */
export function useCostView(
  userId: number | string | null | undefined,
  activeTab: string,
  filters: DashboardFilterState,
  setFilters: (filters: DashboardFilterState) => void,
): UseCostView {
  const [costView, setCostView] = useState<CostView>(() => asCostView(readSessionCostView(userId)) ?? 'future');

  useEffect(() => {
    writeSessionCostView(userId, costView);
  }, [userId, costView]);

  // Not keyed on activeTab: entering Cost is handled by the page's entry effect.
  useEffect(() => {
    if (activeTab !== 'cost') return;
    const next = costViewFilters(costView, filters);
    if (next) setFilters(next);
  }, [costView, filters.dateFrom, filters.dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  return { costView, setCostView };
}
